import GalleryIcon from "@/src/assets/icons/icon-gallery.svg";
import ListIcon from "@/src/assets/icons/icon-menu.svg";
import SearchIcon from "@/src/assets/icons/icon-search.svg";
import { IconButton } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import Table from "@/src/components/atom/Table";
import SectionCard from "@/src/components/molecule/SectionCard";
import GalleryFileCard from "@/src/components/molecule/GalleryFileCard";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import {
  useGetContractsMediasQuery,
  useGetImporterContractsMediasQuery,
  contractDetailApi,
  useLazyGetExporterPoHistoriesQuery,
  useLazyGetExporterScHistoriesQuery,
  useLazyGetImporterPoHistoriesQuery,
  useLazyGetImporterScHistoriesQuery,
} from "@/src/store/apis/contracts/contractDetail";
import { StyledScroll } from "@/src/styles/scroll";
import { AgGridReact } from "ag-grid-react";
import { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import React from "react";
import Typo from "@/src/components/atom/Typo";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import dayjs from "dayjs";
import { ColDef, ValueGetterParams } from "ag-grid-community";
import getTableIndex from "@/src/utils/getTableIndex";

export interface ContractFilesTabProps {
  tab: "list" | "gallery";
  onTabChange: (tab: ContractFilesTabProps["tab"]) => void;
  userType: "EXPORTER" | "IMPORTER";
}

interface VirtualFile {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  service: string;
  mediaUrl: string | null;
  mediaType: string;
  fileExtension: string;
  originalFileName: string;
  folder: string;
  category: string;
  isAttachment: boolean;
}

const ContractFilesTab = ({
  tab,
  onTabChange,
  userType,
}: ContractFilesTabProps) => {
  const { t } = useTranslation();
  const params = useParams();
  const gridRef = useRef<AgGridReact>(null);

  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: "No.",
      headerClass: "ag-right-aligned-cell",
      field: "no",
      lockPosition: "left",
      pinned: "left",
      cellStyle: { textAlign: "center" },
      width: 60,
      resizable: false,
      cellRenderer: function (params: ValueGetterParams) {
        return (
          <Typo typoType="b9r">
            {params.node?.rowIndex
              ? getTableIndex(
                  params.node?.rowIndex + 1,
                  params.data.page,
                  params.data.pageSize,
                )
              : getTableIndex(1, params.data.page, params.data.pageSize)}
          </Typo>
        );
      },
      sortable: false,
    },
    {
      headerClass: "ag-right-aligned-cell",
      cellStyle: { textAlign: "center" },
      field: "type",
      width: 80,
      sortable: false,
    },
    {
      headerClass: "ag-right-aligned-cell",
      cellStyle: { textAlign: "center" },
      field: "category",
      width: 130,
      sortable: false,
    },
    {
      headerClass: "ag-right-aligned-cell",
      cellStyle: { cursor: "pointer", textAlign: "center" },
      field: "originalFileName",
      flex: 1,
      sortable: false,
    },
    {
      headerClass: "ag-right-aligned-cell",
      cellStyle: { textAlign: "center" },
      field: "createdAt",
      width: 150,
      flex: 1,
      sortable: false,
      cellRenderer: (params: ValueGetterParams) => {
        if (params.data.createdAt) {
          return (
            <Typo typoType="b9r">
              {dayjs(params.data.createdAt).format(
                DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
              )}
            </Typo>
          );
        } else {
          return "-";
        }
      },
    },
  ]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [categoryInputValue, setCategoryInputValue] = useState<string>("");
  const [isReady, setIsReady] = useState<boolean>(false);

  // Initialize lazy query hooks
  const [getExporterPoHistories] = useLazyGetExporterPoHistoriesQuery();
  const [getImporterPoHistories] = useLazyGetImporterPoHistoriesQuery();
  const [getExporterScHistories] = useLazyGetExporterScHistoriesQuery();
  const [getImporterScHistories] = useLazyGetImporterScHistoriesQuery();

  // Select appropriate hooks based on user type
  const getPoHistories =
    userType === "EXPORTER" ? getExporterPoHistories : getImporterPoHistories;
  const getScHistories =
    userType === "EXPORTER" ? getExporterScHistories : getImporterScHistories;

  // userType에 따라 적절한 hook 선택
  const useGetMediasQuery =
    userType === "EXPORTER"
      ? useGetContractsMediasQuery
      : useGetImporterContractsMediasQuery;

  // 실제 업로드된 파일들
  const { currentData: mediasData, isError } = useGetMediasQuery(
    { id: Number(params.id) },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // 계약 상세 정보에서 첨부파일 정보 가져오기
  const contractDetailEndpoint =
    userType === "EXPORTER"
      ? contractDetailApi.endpoints.getContractDetail
      : contractDetailApi.endpoints.getImporterContractDetail;

  const contractDetail = contractDetailEndpoint.useQueryState(
    { id: Number(params.id) },
    {
      selectFromResult: ({ currentData }) => currentData,
    },
  );

  // 첨부파일들을 파일 목록 형태로 변환
  const attachmentFiles = useMemo(() => {
    if (!contractDetail) return [];

    const files: VirtualFile[] = [];

    // 실제 업로드된 파일들의 mediaUrl로 중복 체크
    const existingMediaUrls = new Set(
      mediasData?.rows?.map((file: any) => file.mediaUrl) || [],
    );

    // 가상 파일 생성을 위한 설정
    const virtualFileConfigs = [
      {
        type: "PO",
        numberField: "poNo",
        mediaField: "poSimpleMedia",
        category: "PO Document",
      },
      {
        type: "SC",
        numberField: "scNo",
        mediaField: "scSimpleMedia",
        category: "SC Document",
      },
      {
        type: "LC",
        numberField: "lcNo",
        mediaField: "paymentTermsSimpleMedia",
        category: "L/C Document",
      },
    ];

    virtualFileConfigs.forEach((config) => {
      const number = contractDetail[config.numberField];
      const media = contractDetail[config.mediaField];

      if (
        number &&
        number !== "-" &&
        (!media?.mediaUrl || !existingMediaUrls.has(media.mediaUrl))
      ) {
        files.push({
          id: `attachment-${config.type.toLowerCase()}-${contractDetail.id}`,
          createdAt: contractDetail.createdAt,
          updatedAt: contractDetail.updatedAt,
          deletedAt: null,
          service: "BOS_WEB",
          mediaUrl: media?.mediaUrl || null,
          mediaType: "PDF",
          fileExtension: "pdf",
          originalFileName:
            media?.originalFileName || `${config.type}_${number}.pdf`,
          folder: "contracts/",
          category: config.category,
          isAttachment: true,
        });
      }
    });

    return files;
  }, [contractDetail, mediasData]);

  // 전체 파일 목록 (실제 업로드 파일 + 첨부파일)
  const currentData = useMemo(() => {
    const mediaFiles = mediasData?.rows || [];
    const allFiles = [...mediaFiles, ...attachmentFiles];

    return {
      ...mediasData,
      rows: allFiles,
      count: allFiles.length,
    };
  }, [mediasData, attachmentFiles]);

  const categoryInputFilteredData = (currentData?.rows || []).filter(
    ({ category }: any) =>
      category.toLowerCase().includes(categoryInputValue.toLowerCase()),
  );

  useAgGridHeaderRefresh({
    gridRef: gridRef.current,
    isReady,
    headerSet: [
      {
        columnKey: "type",
        langKey: `contract:${userType.toLowerCase()}.detail.fileList.type`,
      },
      {
        columnKey: "category",
        langKey: `contract:${userType.toLowerCase()}.detail.fileList.category`,
      },
      {
        columnKey: "originalFileName",
        langKey: `contract:${userType.toLowerCase()}.detail.fileList.originalFileName`,
      },
      {
        columnKey: "createdAt",
        langKey: `contract:${userType.toLowerCase()}.detail.fileList.createdAt`,
      },
    ],
  });

  const renderFilesByTab = () => {
    switch (tab) {
      case "list":
        return (
          <Table
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={
              isError
                ? []
                : categoryInputFilteredData.slice(
                    (page - 1) * pageSize,
                    page * pageSize,
                  ) || []
            }
            isPaginationDataMaping
            totalPage={currentData?.count || 0}
            page={page}
            pageSize={pageSize}
            handlePaginationClick={(page: number, pageSize: number) => {
              setPage(page);
              setPageSize(pageSize);
            }}
            onGridReady={() => setIsReady(true)}
            onRowClicked={async (gridParams) => {
              // 로우 전체 클릭 시 처리
              const { mediaUrl, isAttachment, fileExtension, category } =
                gridParams.data;

              if (isAttachment && !mediaUrl && fileExtension === "pdf") {
                // PDF 가상 파일: 직접 새 창에서 열기
                try {
                  let documentUrl = null;

                  if (category.includes("SC")) {
                    const scHistories = await getScHistories({
                      contractId: Number(params.id),
                    }).unwrap();
                    if (scHistories?.rows?.length > 0) {
                      documentUrl =
                        scHistories.rows[0].mediaFileUrl ||
                        scHistories.rows[0].documentUrl;
                    }
                  } else if (category.includes("PO")) {
                    const poHistories = await getPoHistories({
                      contractId: Number(params.id),
                    }).unwrap();
                    if (poHistories?.rows?.length > 0) {
                      documentUrl =
                        poHistories.rows[0].mediaFileUrl ||
                        poHistories.rows[0].documentUrl;
                    }
                  } else if (category.includes("L/C")) {
                    // L/C documents might be stored differently, check contractDetail
                    if (contractDetail?.paymentTermsSimpleMedia?.mediaUrl) {
                      documentUrl =
                        contractDetail.paymentTermsSimpleMedia.mediaUrl;
                    }
                  }

                  if (documentUrl) {
                    window.open(documentUrl, "_blank");
                  }
                } catch (error) {
                  console.error("Failed to fetch document URL:", error);
                }
              } else if (mediaUrl) {
                // 실제 파일: 새 창에서 열기
                window.open(mediaUrl, "_blank");
              }
            }}
          />
        );
      case "gallery":
        return (
          <FileGalleryList>
            {categoryInputFilteredData.map((file: any) => {
              const {
                id,
                createdAt,
                mediaUrl,
                originalFileName,
                category,
                isAttachment,
              } = file;

              const handleClick = async () => {
                if (mediaUrl) {
                  window.open(mediaUrl, "_blank");
                } else if (isAttachment && file.fileExtension === "pdf") {
                  // PDF 가상 파일: 직접 새 창에서 열기
                  try {
                    let documentUrl = null;

                    if (category.includes("SC")) {
                      const scHistories = await getScHistories({
                        contractId: Number(params.id),
                      }).unwrap();
                      if (scHistories?.rows?.length > 0) {
                        documentUrl =
                          scHistories.rows[0].mediaFileUrl ||
                          scHistories.rows[0].documentUrl;
                      }
                    } else if (category.includes("PO")) {
                      const poHistories = await getPoHistories({
                        contractId: Number(params.id),
                      }).unwrap();
                      if (poHistories?.rows?.length > 0) {
                        documentUrl =
                          poHistories.rows[0].mediaFileUrl ||
                          poHistories.rows[0].documentUrl;
                      }
                    } else if (category.includes("L/C")) {
                      // L/C documents might be stored differently, check contractDetail
                      if (contractDetail?.paymentTermsSimpleMedia?.mediaUrl) {
                        documentUrl =
                          contractDetail.paymentTermsSimpleMedia.mediaUrl;
                      }
                    }

                    if (documentUrl) {
                      window.open(documentUrl, "_blank");
                    }
                  } catch (error) {
                    console.error("Failed to fetch document URL:", error);
                  }
                }
              };

              return (
                <GalleryFileCard
                  key={id}
                  category={category}
                  fileSrc={mediaUrl || undefined}
                  fileName={originalFileName}
                  uploadedAt={createdAt}
                  onClick={handleClick}
                />
              );
            })}
          </FileGalleryList>
        );
    }
  };

  return (
    <>
      <FlexSpaceBetween>
        <InputContainer>
          <Input
            inputSize="small"
            value={categoryInputValue}
            onChange={({ target: { value } }) => setCategoryInputValue(value)}
            placeholder={t(
              `contract:${userType.toLowerCase()}.detail.placeholder.category`,
            )}
            suffix={<Icon iconSrc={SearchIcon} />}
            onClear={() => setCategoryInputValue("")}
          />
        </InputContainer>

        <Flex>
          {[
            { iconSrc: ListIcon, onClick: () => onTabChange("list") },
            { iconSrc: GalleryIcon, onClick: () => onTabChange("gallery") },
          ].map(({ onClick, iconSrc }) => {
            return (
              <StyledIconButton
                buttonColor="black"
                buttonGrade="tertiary"
                buttonSize={32}
                onClick={onClick}
              >
                <Icon iconSrc={iconSrc} iconSize={16} />
              </StyledIconButton>
            );
          })}
        </Flex>
      </FlexSpaceBetween>

      <SectionCard
        cardTitle={t(
          `contract:${userType.toLowerCase()}.detail.fileList.title`,
        )}
      >
        {renderFilesByTab()}
      </SectionCard>
    </>
  );
};

export default ContractFilesTab;

// Styled Components
const FlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

const InputContainer = styled.div`
  width: 240px;
`;

const Flex = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const FileGalleryList = styled.div`
  display: flex;
  max-height: 520px;
  overflow: auto;
  gap: 16px;
  flex-wrap: wrap;
  ${StyledScroll}
`;
